import { RefObject, useLayoutEffect, useRef } from 'react'

import { animate } from 'framer-motion'
import { useAtomValue } from 'jotai'

import { configFormExpandInteractedAtom } from 'Automations/automationEditor/atoms'
import { CONFIG_FORM_EXPAND_TRANSITION } from 'Automations/automationEditor/constants'

// Animates the config panel's height when it expands/collapses. The panel's
// height is content-sized when collapsed and stretched to fill when expanded —
// neither end is a value CSS can transition between, and framer-motion's
// `layout` would animate the change with a scale transform that visibly smears
// the panel's content. Instead we measure the height before and after the
// layout change (a FLIP) and animate the real pixel height, so the content
// reflows cleanly.
//
// `layoutKey` should change whenever the layout determining the height changes
// (expanded state, toolbar visibility), triggering a re-measure. The animation
// is gated on `configFormExpandInteractedAtom` so the initial mount, content
// loading and node switches snap instantly rather than animating.
export const useConfigFormExpandHeightTransition = (
  ref: RefObject<HTMLElement>,
  layoutKey: string
): void => {
  const hasInteracted = useAtomValue(configFormExpandInteractedAtom)
  const fromHeight = useRef<number | null>(null)
  const previousLayoutKey = useRef(layoutKey)

  // When the layout changes, capture the panel's current height during render —
  // at this point `ref.current` still holds the previously committed DOM, so
  // this is the "before" height for the FLIP. Reading it here rather than in
  // the layout effect (which runs after the new layout commits) keeps it
  // accurate even when the panel resized without re-rendering this component,
  // e.g. the form's content finishing loading after the panel first mounted.
  if (layoutKey !== previousLayoutKey.current) {
    previousLayoutKey.current = layoutKey
    fromHeight.current = ref.current?.getBoundingClientRect().height ?? null
  }

  useLayoutEffect(() => {
    const element = ref.current
    if (!element) {
      return
    }

    const targetHeight = element.getBoundingClientRect().height
    const startHeight = fromHeight.current

    const shouldAnimate =
      hasInteracted && startHeight !== null && startHeight !== targetHeight
    if (!shouldAnimate) {
      return
    }

    // Hand height control back to CSS once the animation is done (or torn down).
    const resetStyles = () => {
      element.style.height = ''
      element.style.overflowY = ''
    }

    // Pin the explicit pixel height and hide overflow for the duration of the
    // animation.
    element.style.overflowY = 'hidden'
    const controls = animate(startHeight, targetHeight, {
      ...CONFIG_FORM_EXPAND_TRANSITION,
      onUpdate: height => {
        element.style.height = `${height}px`
      },
      onComplete: resetStyles
    })

    return () => {
      controls.stop()
      resetStyles()
    }
  }, [ref, layoutKey, hasInteracted])
}
